import type VueRouter from 'vue-router'

export const RELOADED_FOR_CHUNK_KEY = 'blytz:reloaded-for-chunk'

/** Webpack's error for a lazy route chunk that failed to load, usually pruned by a newer deploy. */
export function isChunkLoadError(error: unknown): boolean {
  const { name, message } = (error ?? {}) as { name?: string; message?: string }

  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message ?? '')
  )
}

/**
 * Reloads once at the route whose chunk failed. The guard holds the build id, so the same build
 * never reloads twice and a second failure shows the normal error; a newer build gets one try.
 */
export function installChunkReload(options: {
  router: VueRouter
  buildId: string
  storage: Pick<Storage, 'getItem' | 'setItem'>
  writesInFlight: () => number
  reloadAt: (fullPath: string) => void
}) {
  const { router, buildId, storage } = options
  let target: string | null = null

  router.beforeEach((to, _from, next) => {
    target = to.fullPath
    next()
  })

  // A registered onError replaces vue-router's own console.error, so other errors log here.
  // Offline, or with a save in flight, the error shows and the next click tries again.
  router.onError(error => {
    const canReload =
      globalThis.navigator?.onLine !== false && options.writesInFlight() === 0
    if (
      isChunkLoadError(error) &&
      canReload &&
      target !== null &&
      claimReload()
    ) {
      options.reloadAt(target)

      return
    }
    console.error(error)
  })

  function claimReload(): boolean {
    try {
      if (storage.getItem(RELOADED_FOR_CHUNK_KEY) === buildId) return false
      storage.setItem(RELOADED_FOR_CHUNK_KEY, buildId)

      return true
    } catch {
      // Storage blocked: no guard, so no reload.
      return false
    }
  }
}
